import { StrictMode, Component, type ErrorInfo, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import './index.css'
import './shared/i18n/config'
import { AuthProvider } from './auth/AuthProvider'
import { queryClient } from './shared/queryClient'
import ThemeProvider from './shell/ThemeProvider'
import App from './App'
import { bootstrapTheme } from './store/themeStore'
import { installChunkReloadGuard } from './shell/lazyWithReload'
import { installSessionFetchInterceptor } from './shell/sessionFetchInterceptor'

bootstrapTheme()
// Website-wide fix for the "funny HTML on screen" bug: an expired SWA
// session or a gateway timeout on any /api/* call now surfaces as a German
// error message + the auth providers' SitzungAbgelaufenDialog (§6.59), instead of raw HTML.
installSessionFetchInterceptor()
installChunkReloadGuard()

// Diagnostic root-level ErrorBoundary — logs the React component stack to
// the browser console on any uncaught render error so the persistent
// React #31 (object-as-child) trace can be tied to the actual culprit
// component. Strip once the bug is identified.
class DiagnosticErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) { super(props); this.state = { hasError: false, error: null } }
  static getDerivedStateFromError(error: Error) { return { hasError: true, error } }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.group('[DiagnosticErrorBoundary] Render crash captured')
    console.error('Error:', error?.message)
    console.error('Stack:', error?.stack)
    console.error('Component stack:', info?.componentStack)
    console.groupEnd()
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
          <h2 style={{ color: '#b91c1c' }}>Render-Fehler aufgefangen</h2>
          <p style={{ color: '#475569' }}>Details stehen in der Browser-Konsole — bitte Screenshot der DevTools an Onur senden.</p>
          <pre style={{ background: '#fef2f2', padding: 12, borderRadius: 8, overflow: 'auto' }}>{String(this.state.error?.message ?? '')}</pre>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: 12, padding: '8px 16px', background: '#0f766e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Erneut versuchen
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

window.addEventListener('unhandledrejection', (event) => {
  // eslint-disable-next-line no-console
  console.error('[unhandledrejection]', event.reason)
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <DiagnosticErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ThemeProvider>
    </DiagnosticErrorBoundary>
  </StrictMode>,
)
